import React from 'react';
import classnames from 'classnames';

import { Container } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import GoogleAnalyticsErrorBanner from '@hydra/common/components/GoogleAnalytics/GoogleAnalyticsErrorBanner';
import Review from '@hydra/common/components/Review';
import ReviewDashboard from '@hydra/common/components/ReviewDashboard';
import ReviewFilter from '@hydra/common/components/ReviewFilter';
import ReviewNav from '@hydra/common/components/ReviewNav';
import { ScrollSpyProvider } from '@hydra/common/components/ScrollSpy';
import { useGoogleAnalyticsErrorContext, useReviewContext } from '@hydra/common/context';
import { useReviewFilter } from '@hydra/common/customHooks';
import TrialDaysLeftCta from '@hydra/routes/trial-ended/components/TrialDaysLeftCta';
import { FloatingReviewNav } from '../FloatingReviewNav';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    padding: theme.spacing(0, 2)
  },
  filterWrapper: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 0)
  },
  reviewContainer: {
    width: '100%'
  },
  notificationWrapper: {
    marginTop: theme.spacing(2.5)
  },
  trialBannerWrapper: {
    padding: theme.spacing(3, 0, 0)
  }
}));

export default function ReviewPage (props) {
  const theme = useTheme();
  const {
    criteria,
    outdatedCriteria = {},
    url,
    competitors,
    screenshot,
    score,
    updatedAt,
    progress,
    onpageReviewFilter = null, // Set when filtering from the on-page-assistant
    pdfTemplates,
    bgColor = theme.palette.grey[100],
    reviewStructure,
    fullWidth = false,
    headerHidden = false,
    dudaProject = null,
    upgradeLink = null
  } = props;
  const { isExtension, isAssistant, isLeadgen, isDudaEditor } = useReviewContext();
  const classes = useStyles();
  const {
    handleFilter,
    handleSelect,
    filters,
    showCrit,
    showModule,
    showSection
  } = useReviewFilter({ criteria, reviewStructure });
  const { gaError } = useGoogleAnalyticsErrorContext();

  const showFilters = !(isAssistant && isDudaEditor);
  return (
    <Container
      maxWidth={fullWidth ? false : 'lg'}
      disableGutters
      className={classes.root}
      style={{ backgroundColor: bgColor }}
    >
      <ScrollSpyProvider>
        <div className={classes.reviewContainer}>
          {dudaProject && dudaProject.isPlanTrial && (
            <div className={classes.trialBannerWrapper}>
              <TrialDaysLeftCta days={dudaProject.daysInTrial} upgradeLink={upgradeLink} componentType='span' />
            </div>
          )}
          {gaError &&
            (
              <div className={classes.notificationWrapper}>
                <GoogleAnalyticsErrorBanner gaError={gaError} url={url} />
              </div>
            )}
          {!(isAssistant && onpageReviewFilter) && (
            <ReviewDashboard
              url={url}
              competitors={competitors}
              screenshot={screenshot}
              score={score}
              updatedAt={updatedAt}
              progress={progress}
              pdfTemplates={pdfTemplates}
              outdatedCriteria={outdatedCriteria}
            />
          )}
          {!isLeadgen && !(isAssistant && onpageReviewFilter) && showFilters && (
            <div className={classnames(classes.filterWrapper, 'nr-review-filterbar')}>
              <ReviewFilter
                filters={filters}
                handleFilter={handleFilter}
                handleSelect={handleSelect}
              />
            </div>
          )}
          <Review
            criteria={criteria}
            outdatedCriteria={outdatedCriteria}
            url={url}
            competitors={competitors}
            reviewStructure={reviewStructure}
            showCrit={showCrit}
            showModule={showModule}
            showSection={showSection}
            onpageReviewFilter={onpageReviewFilter}
          />
        </div>
        {!isExtension && !isAssistant && !isLeadgen && (
          <div className={classes.navContainer}>
            <ReviewNav reviewStructure={reviewStructure} showModule={showModule} showSection={showSection} headerHidden={headerHidden} />
          </div>
        )}
        {
          !(onpageReviewFilter === 'average' || onpageReviewFilter === 'bad') && (
            <FloatingReviewNav
              reviewStructure={reviewStructure}
              showModule={showModule}
              showSection={showSection}
              isExtension={isExtension}
              isAssistant={isAssistant}
              isLeadgen={isLeadgen}
            />
          )
        }
      </ScrollSpyProvider>
    </Container>
  );
}
